import { useState, useEffect } from "react";
import { Box, Button, Card, Form, Flex, Text, FormErrorTypes } from "dash-ui";
import { isUrlValid } from "dash-common";
import { useTranslation } from "react-i18next";

import {
  VirtualTourLink,
  VirtualToursSectionProps,
} from "./VirtualToursSection.interfaces";

export const VirtualToursSection = ({
  virtualTourLinks,
  onVirtualTourLinksChange,
  validateOnSave,
}: VirtualToursSectionProps) => {
  const { t } = useTranslation(["common", "forms", "pages"]);

  const [localLinks, setLocalLinks] = useState<VirtualTourLink[]>(
    virtualTourLinks.length > 0 ? virtualTourLinks : [{ url: "", label: "" }]
  );
  const [urlErrors, setUrlErrors] = useState<{ [key: number]: boolean }>({});
  const [labelOnlyErrors, setLabelOnlyErrors] = useState<{
    [key: number]: boolean;
  }>({});

  // Sync local state when parent state changes (e.g., after save)
  useEffect(() => {
    // If parent has links, use them; otherwise show one empty field for new entries
    if (virtualTourLinks.length > 0) {
      setLocalLinks(virtualTourLinks);
    } else {
      setLocalLinks([{ url: "", label: "" }]);
    }
    // Clear any validation errors when syncing with parent state
    setUrlErrors({});
    setLabelOnlyErrors({});
  }, [virtualTourLinks]);

  // Validate all fields when parent requests (before save)
  useEffect(() => {
    if (validateOnSave) {
      const newUrlErrors: { [key: number]: boolean } = {};
      const newLabelErrors: { [key: number]: boolean } = {};

      localLinks.forEach((link, index) => {
        // Check for invalid URLs
        if (link.url && link.url.trim() !== "" && !isUrlValid(link.url)) {
          newUrlErrors[index] = true;
        }
        // Check for label without URL
        if (
          (!link.url || link.url.trim() === "") &&
          link.label &&
          link.label.trim() !== ""
        ) {
          newLabelErrors[index] = true;
        }
      });

      setUrlErrors(newUrlErrors);
      setLabelOnlyErrors(newLabelErrors);
    }
  }, [validateOnSave, localLinks]);

  const handleLinkChange = (
    index: number,
    field: keyof VirtualTourLink,
    value: string
  ) => {
    const updatedLinks = [...localLinks];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setLocalLinks(updatedLinks);

    // Clear label-only error when user adds a URL
    if (field === "url" && value.trim() !== "" && labelOnlyErrors[index]) {
      setLabelOnlyErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }

    // Pass all links to parent, including those with only labels
    // The parent/backend will handle filtering when saving
    onVirtualTourLinksChange(updatedLinks);
  };

  const validateUrl = (index: number, url: string) => {
    // Only validate if there's a value to validate
    if (url) {
      const isValid = isUrlValid(url);
      setUrlErrors((prev) => ({ ...prev, [index]: !isValid }));
    } else {
      // Clear error if field is empty
      setUrlErrors((prev) => ({ ...prev, [index]: false }));
    }
  };

  const addLink = () => {
    const newLinks = [...localLinks, { url: "", label: "" }];
    setLocalLinks(newLinks);
  };

  const removeLink = (index: number) => {
    // Clear error for this index
    setUrlErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      // Adjust indices for remaining errors
      const adjustedErrors: { [key: number]: boolean } = {};
      Object.keys(newErrors).forEach((key) => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          adjustedErrors[keyNum - 1] = newErrors[keyNum];
        } else {
          adjustedErrors[keyNum] = newErrors[keyNum];
        }
      });
      return adjustedErrors;
    });

    if (localLinks.length > 1) {
      const updatedLinks = localLinks.filter((_, i) => i !== index);
      setLocalLinks(updatedLinks);
      onVirtualTourLinksChange(updatedLinks);
    } else {
      // Keep at least one empty field
      const updatedLinks = [{ url: "", label: "" }];
      setLocalLinks(updatedLinks);
      onVirtualTourLinksChange(updatedLinks);
    }
  };

  return (
    <Box mx={6} mt={4}>
      <Flex alignItems="center" justifyContent="space-between" w="100%" mb={4}>
        <Text lg>{t("pages:propertyPage.virtualTours")}</Text>
        <Button type="button" variation="flat" onClick={addLink} icon="plus">
          {t("common:labels.addVirtualTour")}
        </Button>
      </Flex>
      {localLinks.map((link, index) => (
        <Card key={index} mb={4} p={4} shadow="0 1px 3px rgba(0,0,0,0.12)">
          <Flex w="100%" style={{ flexDirection: "row", gap: "12px" }}>
            <Form.Group w="100%">
              <Form.Label required>
                {t("forms:labels.virtualTourUrl", { number: index + 1 })}
              </Form.Label>
              <Form.Input
                value={link.url}
                onChange={(e) => handleLinkChange(index, "url", e.target.value)}
                onBlur={(e) => validateUrl(index, e.target.value)}
                placeholder={t("forms:placeholders.virtualTourUrl") as string}
                $errorType={
                  urlErrors[index] || labelOnlyErrors[index]
                    ? FormErrorTypes.Invalid
                    : undefined
                }
                w="100%"
              />
              <Form.Feedback
                $errorType={
                  urlErrors[index] || labelOnlyErrors[index]
                    ? FormErrorTypes.Invalid
                    : undefined
                }
              >
                {urlErrors[index]
                  ? t("forms:validations.invalidUrl")
                  : labelOnlyErrors[index]
                  ? t("forms:validations.urlRequiredWithLabel")
                  : ""}
              </Form.Feedback>
            </Form.Group>
            <Form.Group w="100%">
              <Form.Label>{t("forms:labels.virtualTourLabel")}</Form.Label>
              <Form.Input
                value={link.label || ""}
                onChange={(e) =>
                  handleLinkChange(index, "label", e.target.value)
                }
                placeholder={t("forms:placeholders.virtualTourLabel") as string}
                w="100%"
              />
            </Form.Group>
            <Flex justifyContent="flex-end">
              <Button
                type="button"
                square
                icon="fillTrash"
                variation="flat"
                mt={6}
                ml={2}
                iconProps={{ size: 24 }}
                colorVariation="danger"
                onClick={() => removeLink(index)}
                disabled={localLinks.length === 1 && !link.url.trim()}
              />
            </Flex>
          </Flex>
        </Card>
      ))}
    </Box>
  );
};

export default VirtualToursSection;
